import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Award, Shield, CheckCircle, Clock, User, Briefcase, AlertTriangle, ExternalLink } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { credentialsAPI } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';

const VerifyCredentialPage = () => {
    const { credentialId } = useParams();
    const [loading, setLoading] = useState(true);
    const [credential, setCredential] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const data = await credentialsAPI.verifyCredential(credentialId);
                if (data?.valid) {
                    setCredential(data.credential);
                } else {
                    setError('This credential could not be verified.');
                }
            } catch {
                setError('Credential not found or verification failed.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [credentialId]);

    const formatDate = (dateValue) => {
        if (!dateValue) return 'N/A';
        const d = dateValue._seconds ? new Date(dateValue._seconds * 1000) : new Date(dateValue);
        return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#08090c] flex items-center justify-center">
                <div className="text-center space-y-4">
                    <Shield className="w-12 h-12 text-purple-400 mx-auto animate-pulse" />
                    <p className="text-slate-400 text-sm">Verifying credential...</p>
                    <LoadingSpinner />
                </div>
            </div>
        );
    }

    if (error || !credential) {
        return (
            <div className="min-h-screen bg-[#08090c] flex items-center justify-center px-4">
                <Helmet>
                    <title>Credential Not Found — Rastor AI</title>
                </Helmet>
                <div className="bg-[#0F131F] border border-red-500/20 rounded-2xl p-8 max-w-sm text-center space-y-4">
                    <AlertTriangle className="w-12 h-12 text-red-400 mx-auto" />
                    <h2 className="text-white font-bold text-lg">Verification Failed</h2>
                    <p className="text-slate-400 text-sm">{error || 'This credential could not be found.'}</p>
                    <Link to="/" className="inline-block px-6 py-2.5 bg-purple-500/10 border border-purple-500/20 rounded-lg text-purple-400 text-sm hover:bg-purple-500/20 transition-colors">
                        Go to Rastor AI
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#08090c] py-12 px-4">
            <Helmet>
                <title>{credential.skill_area} Credential — {credential.user_name} — Rastor AI</title>
                <meta name="description" content={`Verified ${credential.skill_area} credential earned by ${credential.user_name} on Rastor AI. Score: ${credential.score}%`} />
                <meta property="og:title" content={`${credential.skill_area} — Verified Credential`} />
                <meta property="og:description" content={`${credential.user_name} scored ${credential.score}% on the ${credential.skill_area} skill assessment on Rastor AI.`} />
                <meta property="og:type" content="article" />
            </Helmet>

            <div className="max-w-lg mx-auto space-y-6">

                {/* Verified badge header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center space-y-3"
                >
                    <div className="flex items-center justify-center gap-2">
                        <div className="w-8 h-8 bg-emerald-500/10 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-4 h-4 text-emerald-400" />
                        </div>
                        <span className="text-emerald-400 font-mono text-sm uppercase tracking-wider">Verified Credential</span>
                    </div>
                    <p className="text-[11px] text-slate-500 font-mono">ID: {credentialId}</p>
                </motion.div>

                {/* Certificate card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-gradient-to-br from-[#1a1040] via-[#0F131F] to-[#0a1628] border border-purple-500/20 rounded-2xl overflow-hidden"
                >
                    {/* Top decorative bar */}
                    <div className="h-1.5 bg-gradient-to-r from-purple-500 via-violet-500 to-pink-500" />

                    <div className="p-8 space-y-6">
                        {/* Institution */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-purple-500/10 rounded-lg flex items-center justify-center">
                                    <Award className="w-4 h-4 text-purple-400" />
                                </div>
                                <span className="text-white font-bold text-sm">Rastor AI</span>
                            </div>
                            <div className="flex items-center gap-1.5 bg-emerald-500/10 px-2.5 py-1 rounded-full">
                                <Shield className="w-3 h-3 text-emerald-400" />
                                <span className="text-[10px] font-mono text-emerald-400 uppercase">Authentic</span>
                            </div>
                        </div>

                        {/* Skill title */}
                        <div className="text-center py-4">
                            <p className="text-[10px] font-mono text-slate-500 uppercase mb-2">Skill Credential</p>
                            <h1 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-violet-300 to-pink-400">
                                {credential.skill_area}
                            </h1>
                            <p className="text-slate-400 text-sm mt-2">{credential.career_path} Track</p>
                        </div>

                        {/* Awarded to */}
                        <div className="text-center border-t border-white/5 pt-5">
                            <p className="text-[10px] font-mono text-slate-500 uppercase mb-1">Awarded To</p>
                            <p className="text-white text-lg font-bold">{credential.user_name}</p>
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-3 gap-4 bg-[#0B0F19]/60 rounded-xl p-4">
                            <div className="text-center">
                                <p className="text-xl font-bold text-purple-400">{credential.score}%</p>
                                <p className="text-[9px] font-mono text-slate-500 uppercase">Score</p>
                            </div>
                            <div className="text-center border-x border-white/5">
                                <p className="text-xl font-bold text-white">
                                    {credential.questions_correct}/{credential.questions_total}
                                </p>
                                <p className="text-[9px] font-mono text-slate-500 uppercase">Correct</p>
                            </div>
                            <div className="text-center">
                                <p className="text-xl font-bold text-white">
                                    {formatDate(credential.issued_at)}
                                </p>
                                <p className="text-[9px] font-mono text-slate-500 uppercase">Issued</p>
                            </div>
                        </div>

                        {/* Integrity note */}
                        {credential.integrity_flags > 0 && (
                            <div className="flex items-center gap-2 justify-center">
                                <AlertTriangle className="w-3 h-3 text-amber-400/60" />
                                <p className="text-[10px] text-amber-400/60 font-mono">
                                    Completed with {credential.integrity_flags} integrity note{credential.integrity_flags > 1 ? 's' : ''}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Bottom bar */}
                    <div className="bg-[#0B0F19]/50 border-t border-white/5 px-6 py-3 flex items-center justify-between">
                        <p className="text-[10px] text-slate-500 font-mono">rastorai.in/verify/{credentialId}</p>
                        <p className="text-[10px] text-slate-500 font-mono">
                            Week: {credential.week_title}
                        </p>
                    </div>
                </motion.div>

                {/* CTA for visitors */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="text-center space-y-3"
                >
                    <p className="text-slate-500 text-xs">Want to earn verified credentials for your skills?</p>
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-violet-600 rounded-lg text-white text-sm font-medium hover:from-purple-500 hover:to-violet-500 transition-all"
                    >
                        Get Started on Rastor AI
                        <ExternalLink className="w-3.5 h-3.5" />
                    </Link>
                </motion.div>
            </div>
        </div>
    );
};

export default VerifyCredentialPage;
